// ============================================================
// PRODUCTION INVOICE NUMBER ENGINE
// Supports every country format, unlimited companies
// ============================================================

export interface FormatVariables {
  PREFIX: string;
  SUFFIX: string;
  YYYY: string;       // 2026
  YY: string;        // 26
  FY: string;        // 2526 (financial year Apr-Mar)
  FY2: string;       // 25-26
  MM: string;        // 06
  MON: string;       // JUN
  MON3: string;      // Jun
  DD: string;        // 02
  Q: string;         // Q2 (quarter)
  QN: string;        // 2
  WEEK: string;      // W23
  COUNTER: string;   // auto-padded by #### count
  SEQ: string;       // alias for COUNTER
  RAND4: string;     // random 4 digits (for non-sequential)
  CLIENT: string;    // client code
  BRANCH: string;    // branch/location code
}

// Generate all dynamic variables from current date + settings
export const buildFormatVariables = (
  counter: number,
  prefix: string = 'INV',
  suffix: string = '',
  clientCode: string = '',
  branchCode: string = '',
  customDate?: Date
): FormatVariables => {
  const now = customDate || new Date();
  
  const YYYY = String(now.getFullYear());
  const YY = YYYY.slice(2);
  const MM = String(now.getMonth() + 1).padStart(2, '0');
  const DD = String(now.getDate()).padStart(2, '0');
  
  // Financial year: Apr 1 to Mar 31
  const month = now.getMonth(); // 0-indexed
  const year = now.getFullYear();
  const fyStartYear = month >= 3 ? year : year - 1;
  const fyEndYear = fyStartYear + 1;
  const FY = `${String(fyStartYear).slice(2)}${String(fyEndYear).slice(2)}`;
  const FY2 = `${String(fyStartYear).slice(2)}-${String(fyEndYear).slice(2)}`;
  
  const monthNames = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const monthNames3 = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const MON = monthNames[month];
  const MON3 = monthNames3[month];
  
  // Quarter
  const QN = String(Math.ceil((month + 1) / 3));
  const Q = `Q${QN}`;
  
  // Week of year
  const startOfYear = new Date(year, 0, 1);
  const weekNum = Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
  const WEEK = `W${String(weekNum).padStart(2, '0')}`;
  
  const RAND4 = String(Math.floor(1000 + Math.random() * 9000));
  
  return {
    PREFIX: prefix,
    SUFFIX: suffix,
    YYYY, YY, MM, DD,
    FY, FY2,
    MON, MON3,
    Q, QN,
    WEEK,
    COUNTER: String(counter),
    SEQ: String(counter),
    RAND4,
    CLIENT: clientCode.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6),
    BRANCH: branchCode.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4),
  };
};

// Count the # symbols to determine padding
const countPadding = (format: string): number => {
  const match = format.match(/#{2,}/);
  if (!match) return 4;
  return match[0].length;
};

// Core formatter: replaces {VAR} and #### patterns
export const formatInvoiceNumber = (
  format: string,
  variables: FormatVariables
): string => {
  let result = format;
  
  // Replace #### patterns with padded counter
  result = result.replace(/#{2,}/g, (match) => {
    return variables.COUNTER.padStart(match.length, '0');
  });
  
  // Replace {VARIABLE} tokens
  const varMap: Record<string, string> = variables as any;
  result = result.replace(/\{([A-Z0-9_]+)\}/g, (_, key) => {
    return varMap[key] !== undefined ? varMap[key] : `{${key}}`;
  });
  
  return result;
};

// Generate a preview with current date + given counter
export const previewInvoiceFormat = (
  format: string,
  counter: number,
  prefix: string,
  suffix: string,
  clientCode?: string,
  branchCode?: string
): string => {
  const vars = buildFormatVariables(counter, prefix, suffix, clientCode, branchCode);
  return formatInvoiceNumber(format, vars);
};

// ============================================================
// WORLD INVOICE FORMAT PRESETS — 50+ countries/systems
// ============================================================
export const WORLD_FORMAT_PRESETS = [
  // ── INDIA ──────────────────────────────────────────────────
  {
    id: 'india_gst',
    name: 'India GST Standard',
    country: '🇮🇳 India',
    group: 'India',
    format: '{PREFIX}/{FY}-{MM}/####',
    example: 'INV/2526-06/0042',
    description: 'Standard GST invoice. FY = financial year (Apr-Mar), MM = month, 4-digit seq.',
    variables: ['PREFIX', 'FY', 'MM', '####'],
  },
  {
    id: 'india_gst_short',
    name: 'India GST Short Form',
    country: '🇮🇳 India',
    group: 'India',
    format: '{FY}/{PREFIX}-####',
    example: '2526/GST-0001',
    description: 'Compact GST format used by SMEs and freelancers.',
    variables: ['FY', 'PREFIX', '####'],
  },
  {
    id: 'india_fy_serial',
    name: 'India FY/Branch/Serial',
    country: '🇮🇳 India',
    group: 'India',
    format: '{FY}/{BRANCH}/{PREFIX}-######',
    example: '2526/MUM/INV-000042',
    description: 'Multi-branch enterprises with financial year and location code.',
    variables: ['FY', 'BRANCH', 'PREFIX', '######'],
  },

  // ── UAE / GCC ───────────────────────────────────────────────
  {
    id: 'uae_vat',
    name: 'UAE VAT Standard',
    country: '🇦🇪 UAE',
    group: 'Gulf',
    format: '{PREFIX}-{YYYY}-#####',
    example: 'VAT-2026-00042',
    description: 'UAE FTA-compliant VAT invoice. Year + 5-digit sequential.',
    variables: ['PREFIX', 'YYYY', '#####'],
  },
  {
    id: 'uae_vat_monthly',
    name: 'UAE VAT Monthly Reset',
    country: '🇦🇪 UAE',
    group: 'Gulf',
    format: '{PREFIX}/{YYYY}/{MM}/####',
    example: 'VAT/2026/06/0001',
    description: 'Monthly reset counter. Common for high-volume UAE businesses.',
    variables: ['PREFIX', 'YYYY', 'MM', '####'],
  },
  {
    id: 'ksa_vat',
    name: 'KSA ZATCA VAT',
    country: '🇸🇦 Saudi Arabia',
    group: 'Gulf',
    format: '{PREFIX}-{YYYY}{MM}{DD}-#####',
    example: 'ZATCA-20260602-00001',
    description: 'Saudi ZATCA e-invoicing compliant format with full date.',
    variables: ['PREFIX', 'YYYY', 'MM', 'DD', '#####'],
  },
  {
    id: 'gcc_simple',
    name: 'GCC Simple',
    country: '🌍 Gulf Region',
    group: 'Gulf',
    format: '{PREFIX}{YYYY}{MM}####',
    example: 'INV202606001',
    description: 'Compact format for Bahrain, Kuwait, Oman, Qatar.',
    variables: ['PREFIX', 'YYYY', 'MM', '####'],
  },

  // ── USA ────────────────────────────────────────────────────
  {
    id: 'us_simple',
    name: 'US Simple Sequential',
    country: '🇺🇸 USA',
    group: 'Americas',
    format: '{PREFIX}-####',
    example: 'INV-1001',
    description: 'Most common US small business format.',
    variables: ['PREFIX', '####'],
  },
  {
    id: 'us_yearly',
    name: 'US Year-Based',
    country: '🇺🇸 USA',
    group: 'Americas',
    format: '{PREFIX}-{YYYY}-#####',
    example: 'INV-2026-00001',
    description: 'US businesses that reset counter each year.',
    variables: ['PREFIX', 'YYYY', '#####'],
  },
  {
    id: 'us_client_based',
    name: 'US Client-Based',
    country: '🇺🇸 USA',
    group: 'Americas',
    format: '{PREFIX}-{CLIENT}-####',
    example: 'INV-ACME-0042',
    description: 'Agencies and freelancers tracking per-client invoice count.',
    variables: ['PREFIX', 'CLIENT', '####'],
  },

  // ── EU ─────────────────────────────────────────────────────
  {
    id: 'eu_standard',
    name: 'EU Sequential',
    country: '🇪🇺 European Union',
    group: 'Europe',
    format: '{YYYY}-######',
    example: '2026-000042',
    description: 'EU VAT Directive compliant. Year prefix, 6-digit sequential.',
    variables: ['YYYY', '######'],
  },
  {
    id: 'eu_prefix_year',
    name: 'EU Prefix/Year/Seq',
    country: '🇪🇺 European Union',
    group: 'Europe',
    format: '{PREFIX}/{YYYY}/######',
    example: 'INV/2026/000042',
    description: 'Common across Germany, France, Netherlands, Spain.',
    variables: ['PREFIX', 'YYYY', '######'],
  },
  {
    id: 'uk_standard',
    name: 'UK Standard',
    country: '🇬🇧 United Kingdom',
    group: 'Europe',
    format: '{PREFIX}{YYYY}{MM}-#####',
    example: 'INV202606-00001',
    description: 'HMRC-friendly UK invoice numbering.',
    variables: ['PREFIX', 'YYYY', 'MM', '#####'],
  },
  {
    id: 'germany_standard',
    name: 'Germany RE-Number',
    country: '🇩🇪 Germany',
    group: 'Europe',
    format: 'RE-{YYYY}-######',
    example: 'RE-2026-000042',
    description: '"Rechnungsnummer" — German legal invoice standard.',
    variables: ['YYYY', '######'],
  },
  {
    id: 'france_standard',
    name: 'France Facture',
    country: '🇫🇷 France',
    group: 'Europe',
    format: 'F{YYYY}{MM}-####',
    example: 'F202606-0042',
    description: 'French "facture" numbering used by SAS/SARL entities.',
    variables: ['YYYY', 'MM', '####'],
  },
  {
    id: 'italy_fattura',
    name: 'Italy Fattura PA',
    country: '🇮🇹 Italy',
    group: 'Europe',
    format: '{PREFIX}/{YYYY}/####',
    example: 'FT/2026/0042',
    description: 'Italian SDI e-invoicing system (Fattura Elettronica).',
    variables: ['PREFIX', 'YYYY', '####'],
  },
  {
    id: 'spain_factura',
    name: 'Spain Factura',
    country: '🇪🇸 Spain',
    group: 'Europe',
    format: '{PREFIX}-{YYYY}/{MM}-####',
    example: 'FAC-2026/06-0042',
    description: 'Spanish AEAT-compliant invoice series.',
    variables: ['PREFIX', 'YYYY', 'MM', '####'],
  },

  // ── ASIA PACIFIC ────────────────────────────────────────────
  {
    id: 'australia_gst',
    name: 'Australia GST',
    country: '🇦🇺 Australia',
    group: 'Asia Pacific',
    format: '{PREFIX}-{FY2}-####',
    example: 'TAX-25-26-0042',
    description: 'ATO-compliant. Financial year Jul-Jun. FY2 = 25-26 format.',
    variables: ['PREFIX', 'FY2', '####'],
  },
  {
    id: 'singapore_gst',
    name: 'Singapore GST',
    country: '🇸🇬 Singapore',
    group: 'Asia Pacific',
    format: '{PREFIX}-{YYYY}-#####',
    example: 'GST-2026-00042',
    description: 'IRAS-compliant. Annual sequence recommended.',
    variables: ['PREFIX', 'YYYY', '#####'],
  },
  {
    id: 'malaysia_sst',
    name: 'Malaysia SST/e-Invoice',
    country: '🇲🇾 Malaysia',
    group: 'Asia Pacific',
    format: '{PREFIX}/{YYYY}/{MM}/####',
    example: 'INV/2026/06/0042',
    description: 'MyInvois LHDN e-invoice format for Malaysia.',
    variables: ['PREFIX', 'YYYY', 'MM', '####'],
  },
  {
    id: 'japan_standard',
    name: 'Japan Invoice',
    country: '🇯🇵 Japan',
    group: 'Asia Pacific',
    format: '{PREFIX}-{YYYY}{MM}{DD}-####',
    example: 'INV-20260602-0042',
    description: 'Japan Qualified Invoice System (Tekikaku Seikyusho).',
    variables: ['PREFIX', 'YYYY', 'MM', 'DD', '####'],
  },
  {
    id: 'china_standard',
    name: 'China Fapiao',
    country: '🇨🇳 China',
    group: 'Asia Pacific',
    format: '{YYYY}{MM}{DD}-########',
    example: '20260602-00000042',
    description: 'Chinese fapiao (发票) electronic format.',
    variables: ['YYYY', 'MM', 'DD', '########'],
  },
  {
    id: 'india_msme',
    name: 'India MSME / Udyam',
    country: '🇮🇳 India',
    group: 'India',
    format: '{PREFIX}/{FY}/{Q}/####',
    example: 'INV/2526/Q1/0042',
    description: 'Quarterly series for MSME registered entities.',
    variables: ['PREFIX', 'FY', 'Q', '####'],
  },

  // ── CANADA / LATAM ─────────────────────────────────────────
  {
    id: 'canada_hst',
    name: 'Canada GST/HST',
    country: '🇨🇦 Canada',
    group: 'Americas',
    format: '{PREFIX}-{YYYY}-#####',
    example: 'INV-2026-00042',
    description: 'CRA-compliant. Annual sequential.',
    variables: ['PREFIX', 'YYYY', '#####'],
  },
  {
    id: 'brazil_nfe',
    name: 'Brazil NF-e',
    country: '🇧🇷 Brazil',
    group: 'Americas',
    format: 'NF-{YYYY}{MM}-#########',
    example: 'NF-202606-000000042',
    description: 'SEFAZ Nota Fiscal Eletrônica 9-digit sequential.',
    variables: ['YYYY', 'MM', '#########'],
  },
  {
    id: 'mexico_cfdi',
    name: 'Mexico CFDI',
    country: '🇲🇽 Mexico',
    group: 'Americas',
    format: '{PREFIX}-{YYYY}-######',
    example: 'CFDI-2026-000042',
    description: 'SAT Comprobante Fiscal Digital por Internet.',
    variables: ['PREFIX', 'YYYY', '######'],
  },

  // ── AFRICA / MIDDLE EAST ────────────────────────────────────
  {
    id: 'nigeria_firs',
    name: 'Nigeria FIRS',
    country: '🇳🇬 Nigeria',
    group: 'Africa',
    format: '{PREFIX}/{YYYY}/{MM}/####',
    example: 'INV/2026/06/0042',
    description: 'FIRS VAT invoice format for Nigerian businesses.',
    variables: ['PREFIX', 'YYYY', 'MM', '####'],
  },
  {
    id: 'south_africa_vat',
    name: 'South Africa VAT',
    country: '🇿🇦 South Africa',
    group: 'Africa',
    format: '{PREFIX}-{YYYY}{MM}-#####',
    example: 'TAX-202606-00042',
    description: 'SARS VAT invoice requirements.',
    variables: ['PREFIX', 'YYYY', 'MM', '#####'],
  },
  {
    id: 'egypt_vat',
    name: 'Egypt ETA e-Invoice',
    country: '🇪🇬 Egypt',
    group: 'Africa',
    format: '{PREFIX}-{YYYY}-{MM}-######',
    example: 'ETA-2026-06-000042',
    description: 'Egyptian Tax Authority e-invoicing system.',
    variables: ['PREFIX', 'YYYY', 'MM', '######'],
  },

  // ── UNIVERSAL / CUSTOM ──────────────────────────────────────
  {
    id: 'universal_simple',
    name: 'Universal Simple',
    country: '🌍 Global',
    group: 'Universal',
    format: '{PREFIX}-######',
    example: 'INV-000001',
    description: 'Clean simple format works everywhere.',
    variables: ['PREFIX', '######'],
  },
  {
    id: 'universal_date_seq',
    name: 'Universal Date+Seq',
    country: '🌍 Global',
    group: 'Universal',
    format: '{PREFIX}-{YYYY}{MM}-####',
    example: 'INV-202606-0042',
    description: 'Year+month prefix, resets monthly.',
    variables: ['PREFIX', 'YYYY', 'MM', '####'],
  },
  {
    id: 'universal_full_date',
    name: 'Universal Full Date',
    country: '🌍 Global',
    group: 'Universal',
    format: '{PREFIX}-{YYYY}{MM}{DD}-###',
    example: 'INV-20260602-042',
    description: 'Full date prefix. Unique per day.',
    variables: ['PREFIX', 'YYYY', 'MM', 'DD', '###'],
  },
  {
    id: 'freelance_simple',
    name: 'Freelancer Simple',
    country: '🌍 Global',
    group: 'Universal',
    format: '{PREFIX}{YY}{MM}-###',
    example: 'INV2606-042',
    description: 'Compact format for freelancers and sole traders.',
    variables: ['PREFIX', 'YY', 'MM', '###'],
  },
  {
    id: 'client_tracking',
    name: 'Client Code Tracking',
    country: '🌍 Global',
    group: 'Universal',
    format: '{PREFIX}-{CLIENT}-{YYYY}-###',
    example: 'INV-ACME-2026-042',
    description: 'Agencies tracking invoices per client.',
    variables: ['PREFIX', 'CLIENT', 'YYYY', '###'],
  },
  {
    id: 'multi_branch',
    name: 'Multi-Branch Enterprise',
    country: '🌍 Global',
    group: 'Universal',
    format: '{PREFIX}/{BRANCH}/{FY}/######',
    example: 'INV/DXB/2526/000042',
    description: 'Large enterprises with multiple branches/locations.',
    variables: ['PREFIX', 'BRANCH', 'FY', '######'],
  },
  {
    id: 'quarterly_reset',
    name: 'Quarterly Reset',
    country: '🌍 Global',
    group: 'Universal',
    format: '{PREFIX}-{YYYY}-{Q}-####',
    example: 'INV-2026-Q2-0042',
    description: 'Counter resets each quarter.',
    variables: ['PREFIX', 'YYYY', 'Q', '####'],
  },
];

// Group presets by region
export const getPresetsByGroup = () => {
  const groups: Record<string, typeof WORLD_FORMAT_PRESETS> = {};
  WORLD_FORMAT_PRESETS.forEach(p => {
    if (!groups[p.group]) groups[p.group] = [];
    groups[p.group].push(p);
  });
  return groups;
};

// Load/save counter from localStorage per company
export const incrementAndGet = async (): Promise<string> => {
  const { posCounterApi } = await import('./api');
  return posCounterApi.next('INV', 6);
};

// Load/save format config
export const getInvoiceFormatConfig = (companyId: string = 'default') => {
  const key = `invoice_format_config_${companyId}`;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {
      format: '{PREFIX}-######',
      prefix: 'INV',
      suffix: '',
      clientCode: '',
      branchCode: '',
      presetId: 'universal_simple',
    };
  } catch { return null; }
};

export const saveInvoiceFormatConfig = (config: object, companyId: string = 'default') => {
  localStorage.setItem(`invoice_format_config_${companyId}`, JSON.stringify(config));
};
// ============================================================
// SERIAL ELEMENT COUNTERS — enterprise numbering types
// ============================================================
export const SERIAL_TYPES = [
  // Manufacturing & Products
  { key: 'product',      label: 'Product Serial',        prefix: 'SN-',   digits: 6 },
  { key: 'batch',        label: 'Batch / Lot Number',    prefix: 'BAT-',  digits: 6 },
  { key: 'sku',          label: 'SKU / Item Code',       prefix: 'SKU-',  digits: 5 },
  { key: 'asset',        label: 'Asset Tag',             prefix: 'AST-',  digits: 5 },
  { key: 'warranty',     label: 'Warranty Card',         prefix: 'WRT-',  digits: 6 },

  // Logistics & Shipping
  { key: 'shipment',     label: 'Shipment / Tracking',   prefix: 'TRK-',  digits: 8 },
  { key: 'package',      label: 'Package / Box',         prefix: 'PKG-',  digits: 6 },
  { key: 'container',    label: 'Container ID',          prefix: 'CTN-',  digits: 6 },
  { key: 'waybill',      label: 'Airway Bill',           prefix: 'AWB-',  digits: 8 },
  { key: 'delivery',     label: 'Delivery Note',         prefix: 'DN-',   digits: 5 },

  // Finance & Procurement
  { key: 'po',           label: 'Purchase Order',        prefix: 'PO-',   digits: 5 },
  { key: 'quote',        label: 'Quotation',             prefix: 'QT-',   digits: 4 },
  { key: 'receipt',      label: 'Receipt',               prefix: 'RCP-',  digits: 5 },
  { key: 'credit_note',  label: 'Credit Note',           prefix: 'CN-',   digits: 4 },
  { key: 'debit_note',   label: 'Debit Note',            prefix: 'DN-',   digits: 4 },
  { key: 'payment',      label: 'Payment Voucher',       prefix: 'PAY-',  digits: 5 },

  // Real Estate & Construction
  { key: 'property',     label: 'Property / Unit ID',    prefix: 'UNIT-', digits: 4 },
  { key: 'contract',     label: 'Contract Number',       prefix: 'CON-',  digits: 5 },
  { key: 'permit',       label: 'Building Permit',       prefix: 'PRM-',  digits: 5 },
  { key: 'inspection',   label: 'Inspection Report',     prefix: 'INS-',  digits: 5 },

  // Healthcare & Medical
  { key: 'prescription', label: 'Prescription',          prefix: 'RX-',   digits: 6 },
  { key: 'lab',          label: 'Lab / Test Report',     prefix: 'LAB-',  digits: 6 },

  // Legal & Compliance
  { key: 'case',         label: 'Case / File Number',    prefix: 'CASE-', digits: 5 },
  { key: 'license',      label: 'License / Permit',      prefix: 'LIC-',  digits: 5 },

  // Retail & Hospitality
  { key: 'order',        label: 'Sales Order',           prefix: 'ORD-',  digits: 5 },
  { key: 'ticket',       label: 'Support Ticket',        prefix: 'TKT-',  digits: 5 },
  { key: 'booking',      label: 'Booking / Reservation', prefix: 'BKG-',  digits: 5 },
  { key: 'membership',   label: 'Membership Card',       prefix: 'MEM-',  digits: 6 },
  { key: 'loyalty',      label: 'Loyalty / Rewards',     prefix: 'LYL-',  digits: 6 },

  // Education
  { key: 'student',      label: 'Student ID',            prefix: 'STU-',  digits: 6 },
  { key: 'certificate',  label: 'Certificate',           prefix: 'CERT-', digits: 5 },
  { key: 'enrollment',   label: 'Enrollment Number',     prefix: 'ENR-',  digits: 5 },
];
export const getNextSerialNumber = async (type: string): Promise<string> => {
  const serialType = SERIAL_TYPES.find(s => s.key === type);
  const prefix = serialType?.prefix?.replace(/-$/, '') || type.toUpperCase();
  const digits = serialType?.digits || 5;
  const { posCounterApi } = await import('./api');
  return posCounterApi.next(prefix, digits);
};
